import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { filter, Subject, takeUntil, BehaviorSubject, Observable } from "rxjs";
import { when } from "q";
import { userReadWriteGroups } from "../../assets/user-groups";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";
import { AuthenticationResult, EventMessage, EventType } from "@azure/msal-browser";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();
  public isIframe: boolean = false;
  public loggedIn: boolean = false;
  private isRedirecting: boolean = false;
  private redirectAttempts = 0;
  private lastRedirectTime = 0;
  baseGraphUrl = "https://graph.microsoft.com/v1.0";
  
  private readonly CACHE_KEY = 'amigo_user_groups_cache';
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  graphUser: any;
  graphUserGroups: any;
  
  // Observable that emits when user groups are loaded
  private userGroupsLoaded$ = new BehaviorSubject<boolean>(false);
  
  get userGroupsReady$(): Observable<boolean> {
    return this.userGroupsLoaded$.asObservable();
  }

  constructor(
    private httpClient: HttpClient,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private router: Router
  ) {
    this.isIframe = window !== window.parent && !window.opener;

    // Load cached groups immediately if available
    this.loadCachedGroups();
    
    this.setupLoginListeners();
  }

  setupLoginListeners() {
    this.msalService.handleRedirectObservable().pipe(
      takeUntil(this._destroying$)
    ).subscribe(
      (resp) => {
        const activeAccount = this.getActiveAccount();
        
        if (activeAccount === null && !resp) {
          this.userGroupsLoaded$.next(false);
          this.msalService.loginRedirect()
        } else if (resp) {
          // Redirect response received - the account is in the response payload
          this.userGroupsLoaded$.next(false);
          const account = resp.account;
          if (account) {
            this.msalService.instance.setActiveAccount(account);
            this.loggedIn = true;
            this.isRedirecting = false;
            // Fetch groups (will use cache if available and valid)
            if (!this.graphUserGroups) {
              this.getGraphUser();
            }
          } else {
            // No account in redirect response
          }
        } else if (activeAccount) {
          this.loggedIn = true;
          this.isRedirecting = false;
          // Fetch groups only if not already loaded from cache
          if (!this.graphUserGroups) {
            this.getGraphUser();
          }
        }
      }
    )

    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_FAILURE),
      takeUntil(this._destroying$)
    )
      .subscribe(eventMsg => {
        const errorMessage = (eventMsg?.error as any)?.errorMessage || eventMsg?.error?.message || "Unknown error";
        console.error("Login failure:", errorMessage);
        this.loggedIn = false;
      });

    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_SUCCESS),
      takeUntil(this._destroying$)
    )
      .subscribe(eventMsg => {
        const authResult = eventMsg.payload as AuthenticationResult
        this.msalService.instance.setActiveAccount(authResult.account)
        const user = this.getActiveAccount();
        const tokenTenant = user!.idTokenClaims!["tid"];
        const tokenClient = user!.idTokenClaims!["aud"];
        if (!tokenTenant ||
          !tokenClient ||
          tokenTenant !== environment.tenantID ||
          tokenClient !== environment.clientID) {
          this.loggedIn = true;
        } else {
          this.loggedIn = true;
        }
        // Reset redirect tracking on successful login
        this.isRedirecting = false;
        this.redirectAttempts = 0;
      });

    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      takeUntil(this._destroying$)
    )
      .subscribe((eventMsg) => {
        this.loggedIn = true;
      })

    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
      takeUntil(this._destroying$)
    )
      .subscribe((eventMsg) => {
        const error = eventMsg.error;
        const errorCode = (error as any)?.errorCode;
        const errorMessage = (error as any)?.message || '';
        
        // Don't redirect on network errors
        if (errorCode === 'network_error' || errorCode === 'service_unavailable' || 
            errorMessage.includes('timeout') || errorMessage.includes('Network request failed')) {
          return;
        }
        
        // Check if redirect is needed
        const needsRedirect = 
          (error?.name === 'InteractionRequiredAuthError' && this.loggedIn) ||
          (this.getActiveAccount() == null && this.loggedIn);
        
        if (!needsRedirect || this.isRedirecting) return;
        
        const now = Date.now();
        const redirectCooldownMs = 60000;
        const maxRedirectAttempts = 3;
        
        // Apply rate limiting
        if (now - this.lastRedirectTime < redirectCooldownMs) {
          return;
        }
        
        if (this.redirectAttempts >= maxRedirectAttempts) {
          setTimeout(() => { this.redirectAttempts = 0; this.isRedirecting = false; }, 5 * 60 * 1000);
          return;
        }
        
        // Perform redirect
        this.isRedirecting = true;
        this.redirectAttempts++;
        this.lastRedirectTime = now;
        this.msalService.loginRedirect();
        this.loggedIn = false;
      });
  }

  getActiveAccount() {
    return this.msalService.instance.getActiveAccount()
  }

  logout() {
    this.userGroupsLoaded$.next(false);
    this.graphUserGroups = undefined;
    this.graphUser = undefined;
    this.clearCachedGroups();
    this.msalService.logout();
  }

  private loadCachedGroups(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) {
        return;
      }

      const { groups, timestamp, userId } = JSON.parse(cached);
      const activeAccount = this.getActiveAccount();
      
      // Check if cache is valid (not expired, same user)
      const isExpired = Date.now() - timestamp > this.CACHE_EXPIRY_MS;
      const isSameUser = activeAccount?.localAccountId === userId;
      
      if (!isExpired && isSameUser && groups) {
        // Load cached groups silently
        this.graphUserGroups = groups;
        this.userGroupsLoaded$.next(true);
      } else {
        // Clear invalid cache
        this.clearCachedGroups();
      }
    } catch (error) {
      console.error('Failed to load cached groups:', error);
      this.clearCachedGroups();
    }
  }

  private saveCachedGroups(): void {
    try {
      const activeAccount = this.getActiveAccount();
      if (!this.graphUserGroups || !activeAccount) {
        return;
      }

      const cache = {
        groups: this.graphUserGroups,
        timestamp: Date.now(),
        userId: activeAccount.localAccountId
      };
      
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Failed to cache groups:', error);
    }
  }

  private clearCachedGroups(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.error('Failed to clear cached groups:', error);
    }
  }

  getUserName() {
    return this.getActiveAccount() ? this.getActiveAccount()?.name : "";
  }

  getUserNameAndEmail() {
    return this.getActiveAccount() ? (this.getActiveAccount()?.name + " (" + this.getActiveAccount()?.username + ")") : "";
  }

  getGraphUser() {
    const account = this.getActiveAccount();
    
    if (!account || !account.homeAccountId) {
      if (!this.isRedirecting) {
        this.isRedirecting = true;
        this.msalService.loginRedirect();
      }
      return;
    }

    this.httpClient.get(`${this.baseGraphUrl}/me`).subscribe({
      next: (data) => {
        this.graphUser = data;
        when(this.graphUser == data).then(() => this.fetchGraphUserGroups());
      },
      error: (error) => {
        console.error("Http get request to MS Graph failed", error);
      }
    });
  }

  fetchGraphUserGroupsHandler(response: any){
      this.graphUserGroups = response;
      this.userGroupsLoaded$.next(true);
      this.saveCachedGroups();
  }

  handleError(error: any){
    console.error(error);
  }

  fetchGraphUserGroups() {
    this.httpClient
      .get(`${this.baseGraphUrl}/users/${this.graphUser.id}/memberOf`)
      .subscribe({
        next: this.fetchGraphUserGroupsHandler.bind(this),
        error: this.handleError.bind(this)
      });
  }

  hasReadWriteGroups(groups: string[] = userReadWriteGroups.DLI) {
    if (this.graphUserGroups?.value) {
      return this.graphUserGroups.value.findIndex(x => {
          return groups.includes(x.id);
        }) >= 0;
    } else {
      return false;
    }
  }

  hasReadGroups(groups: string[]) {
    // readonly is only relevant for DLI/Trafikinfo page.
    // On kontakter page we want this to return false because that has no read access
    return groups === userReadWriteGroups.DLI;
  }

  async hasGroupsAsync(groups: string[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(
          `${this.baseGraphUrl}/users/${this.getActiveAccount()!.idTokenClaims!["oid"]}/memberOf`
        )
        .subscribe({
          next: (data) => {
            this.graphUserGroups = data;
            if (!this.hasReadWriteGroups(groups) && !this.hasReadGroups(groups)) {
              this.router.navigateByUrl("restricted");
              resolve(false);
            } else {
              resolve(true);
            }
          },
          error: (error) => {
            reject("could not get groups");
            console.error(
              " Http get request to MS Graph failed" + JSON.stringify(error)
            );
          }
        });
    });
  }

  checkContactGroupAccess(): void {
    if (this.graphUserGroups === undefined) {
      if (this.getActiveAccount()) {
        this.findReadWriteGroup(userReadWriteGroups.DLI);
      }
    }
    if (this.getActiveAccount()) {
      if (!this.hasReadWriteGroups(userReadWriteGroups.DLI)) {
        this.router.navigateByUrl("restricted");
      }
    } else {
      this.router.navigateByUrl("restricted");
    }
  }

  findReadWriteGroup(groups: string[]) {
    let userGroups: string[] = this.getActiveAccount()?.idTokenClaims?.groups as string[] || []
    this.graphUserGroups = userGroups.filter( x => groups.includes(x))
  }

  hasAlcolockAccess(): boolean {
    const activeAccount = this.getActiveAccount();
    if (!activeAccount) {
      return false;
    }

    const alcolockGroups = userReadWriteGroups.ALCOLOCK;
    
    const userEmail = activeAccount.username || '';
    if (alcolockGroups.includes(userEmail)) {
      return true;
    }

    if (this.graphUserGroups?.value) {
      return this.graphUserGroups.value.some((group: any) => alcolockGroups.includes(group.id));
    }

    return false;
  }

  hasDriverDeviationAccess(): boolean {
    const activeAccount = this.getActiveAccount();
    if (!activeAccount) {
      return false;
    }

    const driverDeviationGroups = userReadWriteGroups.DRIVER_DEVIATION;
    
    const userEmail = activeAccount.username || '';
    if (driverDeviationGroups.includes(userEmail)) {
      return true;
    }

    if (this.graphUserGroups?.value) {
      return this.graphUserGroups.value.some((group: any) => driverDeviationGroups.includes(group.id));
    }

    return false;
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
